import { Injectable, NgZone, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import uuid from 'uuid-random';

import { ToastMessageType } from './toast-messages.enum';
import { ToastMessage } from './toast-messages.interface';

/**
 * Service to manage creating and removing messages displayed by ToastMessagesComponent.
 */
@Injectable()
export class ToastMessagesServiceBase {
    messages: ToastMessage[];

    messageTypeCss: Record<ToastMessageType, string> = {
        [ToastMessageType.INFO]: 'toast-black',
        [ToastMessageType.ERROR]: 'toast-error',
        [ToastMessageType.NEUTRAL]: 'toast-neutral',
        [ToastMessageType.SIGNAL]: 'toast-signal',
        [ToastMessageType.SUCCESS]: 'toast-success',
    };

    constructor(private sanitizer: DomSanitizer, private ngZone: NgZone) {
        this.messages = [];
    }

    /**
     * Create a toast message that will be automatically removed in `timeout` seconds.
     * If the timeout is 0, then the message will not be automatically removed. To prevent XSS attacks, the `innerHtml`
     * is sanitized as HTML before being added to the message list.
     */
    addMessage(
        innerHtml: string,
        messageType: ToastMessageType = ToastMessageType.INFO,
        timeout: number = 5000,
        icon: string = ''
    ): void {
        const id = uuid();
        const message: ToastMessage = {
            id,
            innerHtml: (this.sanitizer.sanitize(SecurityContext.HTML, innerHtml) as string) || '',
            css: this.messageTypeCss[messageType],
            icon,
        };
        this.ngZone.run(() => {
            this.messages = [message, ...this.messages];
        });

        if (timeout > 0) {
            setTimeout(() => {
                this.removeMessage(id);
            }, timeout);
        }
    }

    removeMessage(id: string): void {
        this.ngZone.run(() => {
            this.messages = this.messages.filter((message: ToastMessage) => message.id !== id);
        });
    }
}
